import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasKnowledgeManagementPermission } from '../utils/knowledgeLotteryAuth';
import { hasPrivateFundPermission } from '../utils/privateFundAuth';
import { hasTravelMemoriesPermission } from '../utils/travelMemoriesAuth';
import { hasHealthPermission } from '../utils/healthAuth';
import './HomePage.css';

/**
 * 应用首页组件
 * 未登录：仅显示羽毛球数据平台 + 登录按钮
 * 已登录：系统管理员显示全部栏目，普通用户按权限显示
 */
const HomePage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const s = localStorage.getItem('user');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [canCourtUsage, setCanCourtUsage] = useState(false);
  const [canPrivateFund, setCanPrivateFund] = useState(false);
  const [canKnowledge, setCanKnowledge] = useState(false);
  const [canTravel, setCanTravel] = useState(false);
  const [canHealth, setCanHealth] = useState(false);

  const isAdmin = currentUser &&
    (currentUser.isAdmin === true || currentUser.isAdmin === 'true');

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {
        setCurrentUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const check = async () => {
      setLoading(true);
      try {
        const timeout = setTimeout(() => setLoading(false), 3000);
        const [p1, p2, p3, p4] = await Promise.all([
          hasPrivateFundPermission(),
          hasKnowledgeManagementPermission(),
          hasTravelMemoriesPermission(),
          hasHealthPermission()
        ]);
        clearTimeout(timeout);
        setCanPrivateFund(p1);
        setCanKnowledge(p2);
        setCanTravel(p3);
        setCanHealth(p4);
        setCanCourtUsage(true);
      } catch {
        setCanCourtUsage(false);
        setCanPrivateFund(false);
        setCanKnowledge(false);
        setCanTravel(false);
        setCanHealth(false);
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [currentUser]);

  const handleClick = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setCanCourtUsage(false);
    setCanPrivateFund(false);
    setCanKnowledge(false);
    setCanTravel(false);
    setCanHealth(false);
    window.location.reload();
  };

  const showCourtUsage = currentUser && (isAdmin || canCourtUsage);
  const showPrivateFund = currentUser && (isAdmin || canPrivateFund);
  const showKnowledge = currentUser && (isAdmin || canKnowledge);
  const showTravel = currentUser && (isAdmin || canTravel);
  const showHealth = currentUser && (isAdmin || canHealth);

  return (
    <div className="home-page">
      <div className="home-header">
        <h1 className="home-title">铖序员</h1>
        <p className="home-subtitle">记录每一个精彩瞬间</p>
        <div
          style={{
            marginTop: '10px',
            fontSize: '14px',
            color: '#666',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 20px'
          }}
        >
          {currentUser ? (
            <>
              <span>当前用户: {currentUser.name || currentUser.email}</span>
              <button
                type="button"
                className="home-logout-btn"
                onClick={handleLogout}
              >
                退出登录
              </button>
            </>
          ) : (
            <>
              <span />
              <button
                type="button"
                className="home-login-btn"
                onClick={() => handleClick('/login')}
              >
                登录
              </button>
            </>
          )}
        </div>
      </div>

      <div className="home-content">
        <div className="platform-card" onClick={() => handleClick('/badminton')}>
          <div className="platform-icon">🏸</div>
          <h2 className="platform-title">进入羽毛球数据平台</h2>
          <p className="platform-description">
            查看比赛记录、球员数据和排名统计，支持多区域数据管理。
          </p>
          <button
            type="button"
            className="platform-button"
            onClick={(e) => { e.stopPropagation(); handleClick('/badminton'); }}
          >
            开始使用
          </button>
        </div>

        {showCourtUsage && (
          <div className="platform-card" onClick={() => handleClick('/court-usage')}>
            <div className="platform-icon">🏟️</div>
            <h2 className="platform-title">场地使用管理</h2>
            <p className="platform-description">
              管理场地充值、使用记录和账户统计。
            </p>
            <button
              type="button"
              className="platform-button"
              onClick={(e) => { e.stopPropagation(); handleClick('/court-usage'); }}
            >
              开始使用
            </button>
          </div>
        )}

        {showPrivateFund && (
          <div className="platform-card" onClick={() => handleClick('/private-fund')}>
            <div className="platform-icon">💰</div>
            <h2 className="platform-title">个人内容</h2>
            <p className="platform-description">
              管理个人产品、净值和收益统计。
            </p>
            <button
              type="button"
              className="platform-button"
              onClick={(e) => { e.stopPropagation(); handleClick('/private-fund'); }}
            >
              开始使用
            </button>
          </div>
        )}

        {showKnowledge && (
          <div className="platform-card" onClick={() => handleClick('/knowledge-lottery')}>
            <div className="platform-icon">📚</div>
            <h2 className="platform-title">知识抽查系统</h2>
            <p className="platform-description">
              管理知识点、成员和抽查会议，进行随机抽签分配。
            </p>
            <button
              type="button"
              className="platform-button"
              onClick={(e) => { e.stopPropagation(); handleClick('/knowledge-lottery'); }}
            >
              开始使用
            </button>
          </div>
        )}

        {showTravel && (
          <div className="platform-card" onClick={() => handleClick('/travel-memories')}>
            <div className="platform-icon">✈️</div>
            <h2 className="platform-title">旅行日志</h2>
            <p className="platform-description">
              记录和分享旅行日志，管理多空间旅行数据，支持照片、行程和地图可视化。
            </p>
            <button
              type="button"
              className="platform-button"
              onClick={(e) => { e.stopPropagation(); handleClick('/travel-memories'); }}
            >
              开始使用
            </button>
          </div>
        )}

        {showHealth && (
          <div className="platform-card" onClick={() => handleClick('/health')}>
            <div className="platform-icon">💊</div>
            <h2 className="platform-title">健康管家</h2>
            <p className="platform-description">
              个人健康管理，每日打卡、体重趋势、方案管理，数据安全存储。
            </p>
            <button
              type="button"
              className="platform-button"
              onClick={(e) => { e.stopPropagation(); handleClick('/health'); }}
            >
              开始使用
            </button>
          </div>
        )}

        {currentUser && loading && (
          <div className="home-loading-tip">正在加载权限…</div>
        )}
      </div>

      <div className="home-footer">
        <p>© 2023 铖序员</p>
      </div>
    </div>
  );
};

export default HomePage; 